import { IBMLocale } from 'src/common/locale/mapValidLocale';

interface UrxFormId {
  id: string;
  name?: string;
}

interface WidgetMetaData {
  formIds: UrxFormId[];
  active: boolean;
}

interface WidgetMetaDataMap {
  [key: string]: WidgetMetaData;
}

/**
 *
 * @param environment
 * @param instanceId
 * @param formId
 * @param urxlang
 * @param bpid
 * @param shortCode - Optional shortCode to dynamically resolve form IDs
 * @param formName - Optional list of form names to match when multiple formIds are present.
 */
export const initUrxForm = async (
  environment: string,
  instanceId: string,
  formId: string,
  urxlang: IBMLocale,
  bpid?: string,
  shortCode?: string,
  setFormID?: React.Dispatch<React.SetStateAction<string>>,
  formName?: string,
) => {
  console.log(
    `Form parameters; environment=`,
    environment,
    'formId=',
    formId,
    'instanceId=',
    instanceId,
  );

  if (
    (document.getElementById(instanceId) as HTMLDivElement).childNodes
      .length === 0 &&
    window.renderUrxWidget
  ) {
    let urxFormId = formId;
    /**
     * If shortCode is present then go for COS call or else skip this block
     */
    if (shortCode) {
      const urxFormIdFromCOS = await getUrxFormIdFromCOS(shortCode, formName);
      /**
       * To check urx form Id is present in COS or not,
       * if not present we fallback to form id for now
       */
      if (urxFormIdFromCOS) {
        console.log('URX Form ID fetched from COS:', urxFormIdFromCOS);
        urxFormId = urxFormIdFromCOS;
      }
    }
    if (setFormID) {
      setFormID(urxFormId);
    }
    console.log('URX Form ID before the urx call:', urxFormId);

    window.urxEnvironment = environment;
    window.loadedUrxForm = false;
    window.renderUrxWidget(
      instanceId,
      urxFormId,
      urxlang,
      {
        column: 3,
        theme: 'light',
        singleStep: true,
        triggerManually: false,
      },
      cb,
      bpid ? bpid : '',
    );
  }
};

function cb(): boolean {
  console.log(`Form was loaded.`);
  window.loadedUrxForm = true;
  return true;
}

/**
 * Fetches a single form ID from COS JSON based on shortcode and optional form names.
 * Logs an error if multiple forms exist but no way to disambiguate.
 * @param shortCode - The shortcode key in the JSON.
 * @param formNames - Optional list of form names to match when multiple formIds are present.
 * @returns A single form ID string, or undefined in case of ambiguity or failure.
 */
const getUrxFormIdFromCOS = async (
  shortCode: string,
  formName?: string,
): Promise<string | undefined> => {
  try {
    const environment =
      window.location.host === 'www.ibm.com' ? 'production' : 'test';

    const response = await fetch(
      `https://ace-project-bucket.s3.us-east.cloud-object-storage.appdomain.cloud/${environment}/urx-form.json`,
    );

    const data: WidgetMetaDataMap = await response.json();

    const widgetMetaData: WidgetMetaData = data[shortCode];

    if (
      !widgetMetaData ||
      !widgetMetaData.active ||
      !Array.isArray(widgetMetaData.formIds)
    ) {
      console.error(`No active form IDs found for shortCode: ${shortCode}`);
      return undefined;
    }

    const formIds = widgetMetaData.formIds;

    // Case 1: Only one form ID present
    if (formIds.length === 1) {
      return formIds[0].id;
    }

    // Case 2: Multiple form IDs and formNames provided
    if (formName) {
      const matchedForms = formIds.filter(
        (form) => form.name && formName.includes(form.name),
      );

      if (matchedForms.length === 1) {
        return matchedForms[0].id;
      }

      console.error(
        `Ambiguous or no match for form name ${formName} under shortCode: ${shortCode}`,
      );
      return undefined;
    }

    // Case 3: Multiple form IDs and no formNames provided
    console.error(
      `Multiple form IDs found for shortCode: ${shortCode}, but no formNames provided to disambiguate.`,
    );
    return undefined;
  } catch (error) {
    console.error('Error while fetching URX form ID from COS:', error);
    return undefined;
  }
};
